import { DBSchema, IDBPDatabase, openDB, StoreKey, StoreNames, StoreValue } from 'idb';
import { Hashed } from '../../../common/src/model.ts';
import { DeadStorageReloadLead, StorageRecoveredAfterReloadLead } from '../../../common/src/storage_report.ts';
import { reportCrashDescription } from '../util/crash.ts';
import { UrlPrefix } from '../util/path.ts';
import {
  DeadStorageReloadDecision,
  decideDeadStorageReload,
  noteStorageWorking,
  scheduleDeadStorageReload,
} from '../util/storage_reload.ts';
import { DeletableSongList } from './song_updates.ts';

// The browser may close the database connection while the page sits idle - WebKit does, after anything
// from minutes to hours in the background (GitHub issue #3906). So no connection is held for the life of
// the page: each operation below gets the current connection just in time, a closed connection is
// dropped, and the next operation opens a new one. When WebKit cannot open a new one either ("Connection
// to Indexed Database server lost", which on iOS before 26.5 is final for the page), the page is reloaded
// through the update gate (util/storage_reload.ts).

const IDBName = 'sing-your-part';
const TestIDBName = 'sing-your-part-test';
const CurrentDBVersion = 2; // bump this number if you add a new store

export const SongListUpdateObjectStore = 'song-list-updates';
export type SongListTimestamp = number; // not an actual date timestamp - a monotonically increasing number

export const DownloadQueueObjectStore = 'download-queue';

export enum DownloadQueueAction {
  Download = 'download',
  Refresh = 'refresh',
  Delete = 'delete',
  DeleteAndDownload = 'delete-and-download',
}

export interface DownloadQueueEntry {
  action: DownloadQueueAction;
  manifestEntry: Hashed;
  // Retry metadata (issue #3517) - OPTIONAL because entries persisted by older bundles have
  // neither field and must be treated as "attempt 0, due now".
  attempts?: number;
  nextAttemptAt?: number; // epoch ms - the entry is not due before this time
}

interface SingYourPartDBSchema extends DBSchema {
  [SongListUpdateObjectStore]: {
    key: SongListTimestamp;
    value: DeletableSongList;
  };
  [DownloadQueueObjectStore]: {
    key: UrlPrefix;
    value: DownloadQueueEntry;
  };
}

type Database = IDBPDatabase<SingYourPartDBSchema>;
type StoreName = StoreNames<SingYourPartDBSchema>;
type Key<Name extends StoreName> = StoreKey<SingYourPartDBSchema, Name>;
type Value<Name extends StoreName> = StoreValue<SingYourPartDBSchema, Name>;

interface CachedConnection {
  promise: Promise<Database>;
  db?: Database; // set once the open succeeds
}

interface ConnectionInfo {
  openedAt: number; // epoch ms
  closeEventAt?: number; // epoch ms - set when the browser's close event fires for this connection
}

// a dead connection, noted until the next operation tells whether it was recovered from
interface DeadConnectionNote {
  notedAt: number; // epoch ms
  details: string;
}

enum DeadConnectionTrigger {
  CloseEvent = 'the browser closed the connection (close event)',
  InvalidStateError = 'transaction() on the cached connection throws InvalidStateError',
  ServerConnectionLost = "the cached connection failed with WebKit's 'Connection to Indexed Database server lost'",
}

const UnrecoveredLead = 'IndexedDB connection is dead and could not be reopened';

// which step of an operation failed - tells an operation aborted on the connection the browser closed apart
// from a fresh connection that fails too, which need different fixes (GitHub issue #3906)
enum FailedStep {
  FirstAttempt = 'the operation on the connection it started with',
  Open = 'opening a new connection',
  Retry = 'the retry on a new connection',
}

interface OperationFailure {
  step: FailedStep;
  operation: string; // e.g. 'get on download-queue'
  error: unknown;
  failedAt: number; // epoch ms
  db?: Database; // the connection the failing attempt ran on - none when the open failed
  firstAttemptError?: unknown; // the InvalidStateError that sent the operation to a new connection
}

// An unrecovered failure waits briefly before it is filed, so that its crash issue can say whether the next
// operation recovered - a second crash issue would send a second email
interface PendingCrashReport {
  lead: string; // the server emails every crash issue except those whose lead marks a handled condition
  failure: OperationFailure;
  deadConnectionDetails: string;
  reload?: string; // what was decided about reloading the page, when the failure called for that decision
  pagehideFired: boolean;
  storagePromise: Promise<string>;
  storage?: string; // filled in when the storage estimate resolves
  timer: ReturnType<typeof setTimeout>;
}

const nextOperationWaitMs = 10_000;
// how long a report filed just before a reload waits for the storage estimate
const storageEstimateWaitMs = 1000;

let currentDatabaseName: string = IDBName;
let cachedConnection: CachedConnection | undefined;
let connectionInfo = new WeakMap<Database, ConnectionInfo>();
let deadConnections = new WeakSet<Database>(); // each dead connection is noted once
let deadConnectionNote: DeadConnectionNote | undefined;
let unrecoveredReported = false; // once per page load, since every crash issue sends an email
let pendingCrashReport: PendingCrashReport | undefined;
let pageClosing = false; // from pagehide until the next pageshow

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    pageClosing = true;
    void fileCrashReport('none before pagehide');
  });
  // a page restored from the back-forward cache is live again, so a dead connection is reported again
  window.addEventListener('pageshow', () => {
    pageClosing = false;
  });
  // Chrome freezes a hidden tab and may then discard it without pagehide, so a waiting report goes out now
  document.addEventListener('freeze', () => void fileCrashReport('none before the page was frozen'));
}

export function idbTestSetup() {
  resetModuleState();
  currentDatabaseName = TestIDBName;
}

export function idbTestTeardown() {
  resetModuleState();
  currentDatabaseName = IDBName;
}

function resetModuleState() {
  // the previous test's connection is closed so it cannot hold up a later version change or delete
  void cachedConnection?.promise.then(
    (db) => db.close(),
    () => {},
  );
  cachedConnection = undefined;
  connectionInfo = new WeakMap();
  deadConnections = new WeakSet();
  deadConnectionNote = undefined;
  unrecoveredReported = false;
  if (pendingCrashReport) {
    clearTimeout(pendingCrashReport.timer);
    pendingCrashReport = undefined;
  }
  pageClosing = false;
}

// For test setup only - app code uses the operations below, which never hold a connection across an await
export function openIDB(): Promise<Database> {
  return getConnection();
}

export function idbGet<Name extends StoreName>(storeName: Name, key: Key<Name>): Promise<Value<Name> | undefined> {
  return runOperation(`get on ${storeName}`, (db) => db.get(storeName, key));
}

export function idbPut<Name extends StoreName>(
  storeName: Name,
  value: Value<Name>,
  key: Key<Name>,
): Promise<Key<Name>> {
  return runOperation(`put on ${storeName}`, (db) => db.put(storeName, value, key));
}

export function idbDelete<Name extends StoreName>(storeName: Name, key: Key<Name>): Promise<void> {
  return runOperation(`delete on ${storeName}`, (db) => db.delete(storeName, key));
}

export function idbGetAllKeys<Name extends StoreName>(storeName: Name): Promise<Key<Name>[]> {
  return runOperation(`getAllKeys on ${storeName}`, (db) => db.getAllKeys(storeName));
}

export function idbClear(storeName: StoreName): Promise<void> {
  return runOperation(`clear on ${storeName}`, (db) => db.clear(storeName));
}

// Runs one operation on the current connection. A connection the browser closed without the close event
// throws InvalidStateError, and one whose IndexedDB server went away fails with WebKit's "Connection to
// Indexed Database server lost", so the operation is run once more on a fresh connection - all five
// operations are safe to run twice. Any other error propagates unchanged; after a dead connection it is
// reported as unrecovered, naming the step that failed, and a fresh connection that fails with WebKit's
// error has the page reloaded.
async function runOperation<T>(operation: string, run: (db: Database) => Promise<T>): Promise<T> {
  const db = await getConnectionReportingFailure(operation);
  let result: T;
  try {
    result = await run(db);
  } catch (e) {
    const trigger = deadConnectionTrigger(e);
    if (!trigger) {
      reportUnrecovered({ step: FailedStep.FirstAttempt, operation, error: e, failedAt: Date.now(), db });
      throw e;
    }
    dropConnection(db);
    noteDeadConnection(db, trigger);
    const freshDb = await getConnectionReportingFailure(operation, e);
    try {
      result = await run(freshDb);
    } catch (retryError) {
      if (deadConnectionTrigger(retryError)) {
        dropConnection(freshDb); // also dead - the next operation starts from a new one
      }
      reportUnrecovered({
        step: FailedStep.Retry,
        operation,
        error: retryError,
        failedAt: Date.now(),
        db: freshDb,
        firstAttemptError: e,
      });
      throw retryError;
    }
  }
  reportStorageWorking();
  noteRecovered(operation);
  return result;
}

async function getConnectionReportingFailure(operation: string, firstAttemptError?: unknown): Promise<Database> {
  try {
    return await getConnection();
  } catch (e) {
    reportUnrecovered({ step: FailedStep.Open, operation, error: e, failedAt: Date.now(), firstAttemptError });
    throw e;
  }
}

// Concurrent first callers share one open. A failed open is dropped, so the next caller tries again.
function getConnection(): Promise<Database> {
  if (cachedConnection) {
    return cachedConnection.promise;
  }
  const connection: CachedConnection = { promise: openConnection() };
  cachedConnection = connection;
  void connection.promise.then(
    (db) => {
      connection.db = db;
    },
    () => {
      if (cachedConnection === connection) {
        cachedConnection = undefined;
      }
    },
  );
  return connection.promise;
}

// async, so that indexedDB.open throwing synchronously becomes a rejection
async function openConnection(): Promise<Database> {
  const db: Database = await openDB<SingYourPartDBSchema>(currentDatabaseName, CurrentDBVersion, {
    upgrade(database) {
      createObjectStoreIfNotExists(database, SongListUpdateObjectStore);
      createObjectStoreIfNotExists(database, DownloadQueueObjectStore);
    },
    // the browser's close event: the connection closed abnormally, e.g. WebKit closing it in the background
    terminated() {
      const info = connectionInfo.get(db);
      if (info) {
        info.closeEventAt = Date.now();
      }
      dropConnection(db);
      noteDeadConnection(db, DeadConnectionTrigger.CloseEvent);
    },
    // another tab wants to open a newer version - let it, and reopen at this version on the next operation
    blocking() {
      db.close();
      dropConnection(db);
    },
  });
  connectionInfo.set(db, { openedAt: Date.now() });
  return db;
}

// only drops the cache if it still holds this connection - a stale close event must not drop a newer one
function dropConnection(db: Database) {
  if (cachedConnection?.db === db) {
    cachedConnection = undefined;
  }
}

// the errors that say the connection an operation ran on is gone, so that a fresh one is worth a try
function deadConnectionTrigger(e: unknown): DeadConnectionTrigger | undefined {
  if (isInvalidStateError(e)) {
    return DeadConnectionTrigger.InvalidStateError;
  }
  if (isServerConnectionLost(e)) {
    return DeadConnectionTrigger.ServerConnectionLost;
  }
  return undefined;
}

function isInvalidStateError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'InvalidStateError';
}

// WebKit's error once the page's connection to its IndexedDB server (the Networking process, which iOS
// kills in the background) is gone. On WebKit before iOS 26.5 the page cannot open another connection
// (WebKit bug 309386), which is what "Refresh the page to try again" means, so a fresh connection that
// fails with it has the page reloaded.
function isServerConnectionLost(e: unknown): boolean {
  return (
    (e instanceof DOMException || e instanceof Error) &&
    e.message.includes('Connection to Indexed Database server lost')
  );
}

// Reporting, GitHub issue #3906. A dead connection is sorted into three cases:
// - the page is closing: expected, nothing is reported
// - the next operation succeeds on a fresh connection: recovered, written to the console
// - an operation still fails: unexpected, filed as a crash issue (the first one per page load) that names the
//   failed step and says whether the next operation recovered
function noteDeadConnection(db: Database, trigger: DeadConnectionTrigger) {
  if (pageClosing || deadConnections.has(db)) {
    return;
  }
  deadConnections.add(db);
  const now = Date.now();
  const info = connectionInfo.get(db);
  const details =
    `${trigger}. ` +
    `Connection opened ${info ? Math.round((now - info.openedAt) / 1000) : '?'} s ago, ` +
    `page loaded ${Math.round((now - performance.timeOrigin) / 1000)} s ago, ` +
    `close event ${info?.closeEventAt !== undefined ? 'fired' : 'not fired'}, ` +
    `page ${document.visibilityState}.`;
  deadConnectionNote = { notedAt: now, details };
  console.log(`IndexedDB connection is dead (GitHub issue #3906): ${details}`);
}

function noteRecovered(operation: string) {
  if (pendingCrashReport) {
    void fileCrashReport(`${operation} succeeded ${secondsSince(pendingCrashReport.failure.failedAt)} s later`);
  }
  if (!deadConnectionNote) {
    return;
  }
  console.log(
    `IndexedDB connection reopened (GitHub issue #3906) ${secondsSince(deadConnectionNote.notedAt)} s after it ` +
      `was found dead: ${deadConnectionNote.details}`,
  );
  deadConnectionNote = undefined;
}

function reportUnrecovered(failure: OperationFailure) {
  // a fresh connection failing with WebKit's error means this page cannot reopen the database at all, so
  // the page is reloaded - whether or not the connection before it was seen to die
  const reload =
    failure.step !== FailedStep.FirstAttempt && isServerConnectionLost(failure.error) && !pageClosing
      ? decideDeadStorageReload()
      : undefined;
  if (!deadConnectionNote && !reload) {
    return;
  }
  const details = deadConnectionNote?.details ?? 'No dead connection had been noted.';
  const lead = reload?.kind === 'scheduled' ? DeadStorageReloadLead : UnrecoveredLead;
  const reloadDescription = reload && describeReloadDecision(reload);
  console.log(
    `${lead} (GitHub issue #3906): ${details} ${describeFailure(failure)}${reloadDescription ? ` ${reloadDescription}` : ''}`,
  );
  if (pendingCrashReport) {
    if (reload) {
      // the waiting report is about an earlier failure; this one decided about the reload, so it says so
      pendingCrashReport.lead = lead;
      pendingCrashReport.reload = reloadDescription;
    }
    void fileCrashReport(
      `${failure.operation} failed too, ${secondsSince(pendingCrashReport.failure.failedAt)} s later, at ` +
        `${failure.step}: ${describeError(failure.error)}`,
    );
  } else if (!unrecoveredReported) {
    unrecoveredReported = true;
    const report: PendingCrashReport = {
      lead,
      failure,
      deadConnectionDetails: details,
      reload: reloadDescription,
      pagehideFired: pageClosing,
      storagePromise: describeStorage(),
      timer: setTimeout(() => void fileCrashReport(`none within ${nextOperationWaitMs / 1000} s`), nextOperationWaitMs),
    };
    pendingCrashReport = report;
    void report.storagePromise.then((storage) => {
      report.storage = storage;
    });
  }
  if (reload?.kind === 'scheduled') {
    scheduleDeadStorageReload(fileCrashReportBeforeReload);
  }
}

// The waiting report, if any, goes out before the reload takes the page away - with the storage estimate,
// which separates this failure from eviction, if it arrives in time: the reload can fire in the same tick
// as the failure.
async function fileCrashReportBeforeReload(): Promise<void> {
  const report = pendingCrashReport;
  if (report) {
    await Promise.race([report.storagePromise, new Promise((resolve) => setTimeout(resolve, storageEstimateWaitMs))]);
  }
  await fileCrashReport('none before the page reloaded');
}

function describeReloadDecision(decision: DeadStorageReloadDecision): string {
  switch (decision.kind) {
    case 'scheduled':
      return `Reload: ${decision.timing}.`;
    case 'already scheduled':
      return 'Reload: already scheduled.';
    case 'skipped':
      return `Reload: skipped, ${decision.reason}.`;
  }
}

// the first success after a dead-storage reload is what shows that the reload helped
function reportStorageWorking() {
  const recovery = noteStorageWorking();
  if (!recovery) {
    return;
  }
  void reportCrashDescription(
    `${StorageRecoveredAfterReloadLead} (GitHub issue #3906): ${recovery}. User agent: ${navigator.userAgent}.`,
  );
}

// files the waiting crash issue, if any, with what became of the next operation; resolves once it is sent
function fileCrashReport(nextOperation: string): Promise<void> {
  const report = pendingCrashReport;
  if (!report) {
    return Promise.resolve();
  }
  clearTimeout(report.timer);
  pendingCrashReport = undefined;
  return reportCrashDescription(
    `${report.lead} (GitHub issue #3906): ${report.deadConnectionDetails} ` +
      `${describeFailure(report.failure)} ` +
      `Next operation: ${nextOperation}. ` +
      `${report.reload ? `${report.reload} ` : ''}` +
      `${report.storage ?? 'Storage estimate not available'}. ` +
      `User agent: ${navigator.userAgent}. ` +
      `pagehide ${report.pagehideFired ? 'fired' : 'not fired'}.`,
  );
}

// Described when the report is filed, so a close event that arrived after the failure is included
function describeFailure({ step, operation, error, failedAt, db, firstAttemptError }: OperationFailure): string {
  let description = `Failed step: ${step} (${operation}): ${describeError(error)}.`;
  if (firstAttemptError !== undefined) {
    description += ` The first attempt had failed with ${describeError(firstAttemptError)}.`;
  }
  return `${description} ${describeConnectionUsed(db, failedAt)}`;
}

function describeConnectionUsed(db: Database | undefined, failedAt: number): string {
  if (!db) {
    return 'No connection was open.';
  }
  const info = connectionInfo.get(db);
  if (!info) {
    return 'The connection it ran on is unknown.';
  }
  const opened = `The connection it ran on was opened ${Math.round((failedAt - info.openedAt) / 1000)} s before`;
  if (info.closeEventAt === undefined) {
    return `${opened}, and no close event has fired for it.`;
  }
  const closeEventMs = info.closeEventAt - failedAt;
  if (closeEventMs === 0) {
    return `${opened}, and its close event fired in the same millisecond as the failure.`;
  }
  return `${opened}, and its close event fired ${Math.abs(closeEventMs)} ms ${closeEventMs < 0 ? 'before' : 'after'} the failure.`;
}

// a browser evicting the site's storage under disk pressure closes its connections, so the report says
// whether storage is persisted and how full it is
async function describeStorage(): Promise<string> {
  try {
    const storage: StorageManager | undefined = navigator.storage;
    if (!storage) {
      return 'Storage API not available';
    }
    const [persisted, { usage, quota }] = await Promise.all([storage.persisted(), storage.estimate()]);
    return `Storage ${persisted ? 'persisted' : 'not persisted'}, ${megabytes(usage)} MB used of a ${megabytes(quota)} MB quota`;
  } catch (e) {
    return `Storage estimate failed: ${describeError(e)}`;
  }
}

function megabytes(bytes: number | undefined): string {
  return bytes === undefined ? '?' : String(Math.round(bytes / 1_000_000));
}

function secondsSince(epochMs: number): number {
  return Math.round((Date.now() - epochMs) / 1000);
}

function describeError(error: unknown): string {
  if (error instanceof Error || error instanceof DOMException) {
    return `${error.name}: ${error.message.replace(/\.$/, '')}`;
  }
  return String(error);
}

function createObjectStoreIfNotExists(db: Database, name: StoreName) {
  if (!db.objectStoreNames.contains(name)) {
    console.log(`Creating IDB object store ${name}`);
    db.createObjectStore(name);
  }
}
